/**
 * ZTNA Customers admin page (route: /admin/ztna/customers).
 *
 * v2 (Spec §10.3): customers sao globais (sem tenant_id) e acesso e dado
 * via grants por aplicacao. A pagina lista todos os customers visiveis
 * ao admin logado, com filtros opcionais por tenant e status. Detalhes
 * por customer (ambientes, expiracao por grant, sessoes, auditoria) ficam
 * no drawer CustomerDetail.
 *
 * Permission: ztna_customer:read
 */
import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  Container,
  Group,
  Select,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { IconUserPlus, IconUsers } from '@tabler/icons-react'
import { Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { useCan, useUserTenants } from '@/store/auth'
import { EmptyState } from '@/components/ui/EmptyState'
import { DashboardSkeleton } from '@/components/ui/DashboardSkeleton'
import {
  ztnaCustomersApi,
  type CustomerStatus,
  type ZtnaCustomer,
} from '@/api/ztnaCustomers'

import { CustomerDrawer } from '@/components/ZtnaAdmin/Customers/CustomerDrawer'
import { CustomerDetail } from '@/components/ZtnaAdmin/Customers/CustomerDetail'
import { formatApiError } from '@/api/errors'

dayjs.extend(relativeTime)

const PAGE_SIZE = 50

// ─── Status helpers ────────────────────────────────────────────────────────────

function statusColor(status: CustomerStatus): string {
  switch (status) {
    case 'active':           return 'teal'
    case 'pending_password': return 'yellow'
    case 'revoked':          return 'red'
    default:                 return 'gray'
  }
}

function statusLabel(status: CustomerStatus): string {
  switch (status) {
    case 'active':           return 'Ativo'
    case 'pending_password': return 'Aguardando senha'
    case 'revoked':          return 'Revogado'
    default:                 return status
  }
}

// ─── Status filter options (sem 'expired' em v2) ──────────────────────────────

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '',                 label: 'Todos os status' },
  { value: 'active',           label: 'Ativo' },
  { value: 'pending_password', label: 'Aguardando senha' },
  { value: 'revoked',          label: 'Revogado' },
]

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function ZtnaCustomers() {
  const can = useCan()
  const userTenants = useUserTenants()

  // Permission gate
  if (!can('ztna_customer', 'read')) {
    return <Navigate to="/" replace />
  }

  const [statusFilter, setStatusFilter] = useState<string>('')
  const [tenantFilter, setTenantFilter] = useState<string>('')
  const [page, setPage] = useState(0)

  // createDrawerOpen: true opens CustomerDrawer in create mode
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false)
  // editCustomer: non-null opens CustomerDrawer in edit mode
  const [editCustomer, setEditCustomer] = useState<ZtnaCustomer | null>(null)
  // detailCustomer: non-null opens CustomerDetail
  const [detailCustomer, setDetailCustomer] = useState<ZtnaCustomer | null>(null)

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['ztna', 'customers', { tenantFilter, statusFilter, page }],
    queryFn: () =>
      ztnaCustomersApi.listCustomers({
        tenantId: tenantFilter ? Number(tenantFilter) : undefined,
        status: statusFilter ? (statusFilter as CustomerStatus) : undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  })

  // Sync detailCustomer com listing apos mutations (revoke/reactivate/grants).
  // Sem isso, o drawer renderiza snapshot estatico e nao reflete mudancas de
  // status do customer ou grants. Disparado quando o listing query refeza
  // (mutations invalidate ['ztna', 'customers']).
  useEffect(() => {
    if (detailCustomer && data?.items) {
      const updated = data.items.find(c => c.id === detailCustomer.id)
      if (updated && updated !== detailCustomer) {
        setDetailCustomer(updated)
      }
    }
  }, [data, detailCustomer])

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <DashboardSkeleton />
      </Container>
    )
  }

  // ── Error ────────────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <Container size="xl" py="md">
        <EmptyState
          icon={IconUsers}
          title="Erro ao carregar clientes"
          description={
            formatApiError(error, 'Não foi possível carregar a lista de clientes ZTNA.')
          }
          accent="danger"
          secondaryAction={{ label: 'Tentar novamente', onClick: () => refetch() }}
        />
      </Container>
    )
  }

  const rows = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const isEmpty = rows.length === 0
  const hasActiveFilter = !!statusFilter || !!tenantFilter

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="center">
          <Title order={3}>Clientes ZTNA</Title>
          {can('ztna_customer', 'create') && (
            <Button
              size="sm"
              leftSection={<IconUserPlus size={16} />}
              color="teal"
              onClick={() => setCreateDrawerOpen(true)}
            >
              Novo cliente
            </Button>
          )}
        </Group>

        {/* Filters: tenant + status */}
        <Group gap="sm">
          <Select
            size="sm"
            w={260}
            label="Tenant"
            placeholder="Todos os tenants"
            data={[
              { value: '', label: 'Todos os tenants' },
              ...userTenants.map(t => ({ value: String(t.id), label: t.name })),
            ]}
            value={tenantFilter}
            onChange={v => {
              setTenantFilter(v ?? '')
              setPage(0)
            }}
            clearable={false}
          />
          <Select
            size="sm"
            w={220}
            label="Status"
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v ?? '')
              setPage(0)
            }}
            clearable={false}
          />
        </Group>

        {/* Empty state */}
        {isEmpty ? (
          <EmptyState
            icon={IconUsers}
            title="Nenhum cliente encontrado"
            description={
              hasActiveFilter
                ? 'Não há clientes com os filtros aplicados.'
                : 'Cadastre o primeiro cliente para permitir acesso externo via ZTNA.'
            }
            primaryAction={
              can('ztna_customer', 'create')
                ? {
                    label: 'Cadastrar cliente',
                    icon: <IconUserPlus size={16} />,
                    onClick: () => setCreateDrawerOpen(true),
                  }
                : undefined
            }
            secondaryAction={
              hasActiveFilter
                ? {
                    label: 'Limpar filtros',
                    onClick: () => {
                      setStatusFilter('')
                      setTenantFilter('')
                      setPage(0)
                    },
                  }
                : undefined
            }
          />
        ) : (
          <>
            {/* Table */}
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>E-mail</Table.Th>
                  <Table.Th>Nome</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Apps autorizadas</Table.Th>
                  <Table.Th>Última atividade</Table.Th>
                  <Table.Th w={80}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map(customer => {
                  const activeGrants = customer.grants.filter(
                    g => !g.revoked_at,
                  )
                  const hostnamesPreview = activeGrants
                    .slice(0, 3)
                    .map(g => g.application.hostname)
                    .join(', ')
                  const moreLabel = activeGrants.length > 3 ? '…' : ''
                  const tooltipLabel = hostnamesPreview + moreLabel

                  return (
                    <Table.Tr key={customer.id}>
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm" ff="monospace">
                            {customer.email}
                          </Text>
                          {customer.email_bounced && (
                            <Tooltip label="E-mail com falha de entrega">
                              <Badge size="xs" color="red" variant="filled">
                                falha
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{customer.full_name}</Text>
                        {customer.company && (
                          <Text size="xs" c="dimmed">
                            {customer.company}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="sm"
                          color={statusColor(customer.status)}
                          variant="light"
                        >
                          {statusLabel(customer.status)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip
                          label={tooltipLabel}
                          disabled={activeGrants.length === 0}
                        >
                          <Badge size="sm" variant="light" color="teal">
                            {activeGrants.length}
                          </Badge>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        {customer.last_session_at ? (
                          <Tooltip
                            label={dayjs(customer.last_session_at).format(
                              'YYYY-MM-DD HH:mm',
                            )}
                          >
                            <Text size="xs" c="dimmed">
                              {dayjs(customer.last_session_at).fromNow()}
                            </Text>
                          </Tooltip>
                        ) : (
                          <Text size="xs" c="dimmed">
                            sem sessão
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Button
                          size="xs"
                          variant="default"
                          onClick={() => setDetailCustomer(customer)}
                        >
                          Detalhes
                        </Button>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>

            {/* Pagination */}
            {total > PAGE_SIZE && (
              <Group justify="center" gap="sm">
                <Button
                  size="xs"
                  variant="default"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  « Anterior
                </Button>
                <Text size="xs" c="dimmed">
                  Página {page + 1} de {totalPages} ({total} cliente
                  {total !== 1 ? 's' : ''})
                </Text>
                <Button
                  size="xs"
                  variant="default"
                  disabled={(page + 1) * PAGE_SIZE >= total}
                  onClick={() => setPage(p => p + 1)}
                >
                  Próxima »
                </Button>
              </Group>
            )}

            {/* Row count (when no pagination) */}
            {total <= PAGE_SIZE && (
              <Text size="xs" c="dimmed" ta="right">
                {total} cliente{total !== 1 ? 's' : ''} listado
                {total !== 1 ? 's' : ''}
              </Text>
            )}
          </>
        )}
      </Stack>

      {/* CustomerDrawer — create mode */}
      <CustomerDrawer
        customer={null}
        opened={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
      />

      {/* CustomerDrawer — edit mode */}
      {editCustomer && (
        <CustomerDrawer
          customer={editCustomer}
          opened={editCustomer != null}
          onClose={() => setEditCustomer(null)}
        />
      )}

      {/* CustomerDetail drawer */}
      <CustomerDetail
        customer={detailCustomer}
        opened={detailCustomer != null}
        onClose={() => setDetailCustomer(null)}
        onEdit={c => {
          setDetailCustomer(null)
          setEditCustomer(c)
        }}
      />
    </Container>
  )
}
